
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

// Create a schema for email validation
const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

interface CheckoutDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  setEmail: (email: string) => void;
  emailError: string;
  formatReleaseDate: () => string;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting?: boolean;
}

const CheckoutDialog: React.FC<CheckoutDialogProps> = ({
  isOpen,
  onOpenChange,
  email,
  setEmail,
  emailError,
  formatReleaseDate,
  onSubmit,
  isSubmitting = false
}) => {
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [processingTimeout, setProcessingTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Initialize the form with react-hook-form and zod validation
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email || "",
    },
  });

  // Clear timeout when component unmounts or dialog closes
  useEffect(() => {
    return () => {
      if (processingTimeout) {
        clearTimeout(processingTimeout);
      }
    };
  }, [processingTimeout]);

  // Reset form state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormSubmitting(false);
      if (processingTimeout) {
        clearTimeout(processingTimeout);
        setProcessingTimeout(null);
      }
    }
    
    if (isOpen) {
      form.reset({ email: email || "" });
    }
  }, [isOpen, email, form, processingTimeout]);

  // Handle form submission with validation
  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      // Prevent multiple submissions
      if (formSubmitting || isSubmitting) return;
      
      setFormSubmitting(true);
      setEmail(values.email);
      
      // Set a timeout to show error if processing takes too long (20 seconds)
      const timeout = setTimeout(() => {
        toast({
          title: "Processing timeout",
          description: "The checkout process is taking longer than expected. Please try again.",
          variant: "destructive"
        });
        setFormSubmitting(false);
        onOpenChange(false);
      }, 20000); // Increased from 12s to 20s to give more time for Stripe to respond
      
      setProcessingTimeout(timeout);
      
      // Call the parent component's submit handler
      const mockEvent = { preventDefault: () => {} } as React.FormEvent;
      await onSubmit(mockEvent);
      
      // Clear timeout if successful
      if (processingTimeout) {
        clearTimeout(processingTimeout);
        setProcessingTimeout(null);
      }
      
      // The dialog will be closed by the parent component after successful checkout
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Error",
        description: "Failed to start checkout process. Please try again.",
        variant: "destructive"
      });
      setFormSubmitting(false);
      
      // Clear timeout if there's an error
      if (processingTimeout) {
        clearTimeout(processingTimeout);
        setProcessingTimeout(null);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Prevent closing the dialog while submitting unless explicitly requested
      if ((formSubmitting || isSubmitting) && open === false) {
        if (window.confirm("Are you sure you want to cancel the checkout process?")) {
          if (processingTimeout) {
            clearTimeout(processingTimeout);
            setProcessingTimeout(null);
          }
          setFormSubmitting(false);
          onOpenChange(false);
        }
      } else {
        onOpenChange(open);
      }
    }}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Enter Your Email to Continue</DialogTitle>
          <DialogDescription>
            You'll receive course access and payment confirmation at this email address.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email address</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="your@email.com" 
                      type="email"
                      autoComplete="email"
                      {...field} 
                      autoFocus
                      disabled={formSubmitting || isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-xs text-muted-foreground mt-1">
                    You'll receive course access details on {formatReleaseDate()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    After payment, you'll be able to create an account to access the course.
                  </p>
                </FormItem>
              )}
            />
            
            <DialogFooter className="mt-4 gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  if (processingTimeout) {
                    clearTimeout(processingTimeout);
                    setProcessingTimeout(null);
                  }
                  setFormSubmitting(false);
                  onOpenChange(false);
                }}
                disabled={formSubmitting && !processingTimeout}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={formSubmitting || isSubmitting}
                className="bg-techne-purple hover:bg-techne-deepPurple"
              >
                {(formSubmitting || isSubmitting) ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Proceed to Payment"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutDialog;
